import { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Form, Modal } from "react-bootstrap";
import HeaderNavbar from "./Navbar";
import Footer from "./Footer";
import { useNavigate, Link } from "react-router-dom";
import Login from "../Component/Login";
import { userRegister } from "../store/slices/register";
import { useDispatch } from "react-redux";
import { userVerifyOtp } from "../store/slices/auth";
import { toast } from "react-toastify";

const getProfileForOptions = () => {
  return [
    "Myself",
    "Son",
    "Daughter",
    "Brother",
    "Sister",
    "Friend",
  ];
};

const getGenderOptions = (profileFor) => {
  if (profileFor === "Son" || profileFor === "Brother") {
    return ["Male"];
  }

  if (profileFor === "Daughter" || profileFor === "Sister") {
    return ["Female"];
  }

  return ["Male", "Female"];
};

const getNameLabel = (profileFor) => {
  switch (profileFor) {
    case "Son":
      return "Son's Name";
    case "Daughter":
      return "Daughter's Name";
    case "Brother":
      return "Brother's Name";
    case "Sister":
      return "Sister's Name";
    case "Friend":
      return "Friend's Name";
    default:
      return "Full Name";
  }
};

export default function RegisterForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const otpRefs = useRef([]);

  const [showLogin, setShowLogin] = useState(false);
  const [registeredUserId, setRegisteredUserId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    dateofbirth: "",
    gender: "",
    profileFor: "",
    email: "",
  });

  const [errors, setErrors] = useState({});
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpError, setOtpError] = useState("");

  const setValue = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    validateField(field, value);
  };

  const validateField = (field, value) => {
    let message = "";

    if (field === "name") {
      if (!value.trim()) message = "Please enter full name";
      else if (!/^[A-Za-z\s]+$/.test(value))
        message = "Name cannot contain numbers";
    }

    if (field === "phone") {
      if (!value) message = "Please enter phone number";
      else if (!/^\d{10}$/.test(value))
        message = "Please enter valid phone number";
    }

    if (field === "dateofbirth") {
      if (!value) {
        message = "Please enter date of birth";
      } else {
        const dob = new Date(value);
        const today = new Date();

        let age = today.getFullYear() - dob.getFullYear();
        const monthDiff = today.getMonth() - dob.getMonth();

        if (
          monthDiff < 0 ||
          (monthDiff === 0 && today.getDate() < dob.getDate())
        ) {
          age--;
        }

        const minAge = form.gender === "Male" ? 21 : 18;

        if (age < minAge) {
          message = `Minimum age is ${minAge}`;
        } else if (age > 55) {
          message = "Maximum age is 55";
        }
      }
    }

    if (field === "gender") {
      if (!value) message = "Please select gender";
    }

    if (field === "profileFor") {
      if (!value) message = "Please select profile for";
    }

    if (field === "email") {
      if (!value) message = "Please enter email";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
        message = "Please enter valid email";
    }

    setErrors((prev) => ({ ...prev, [field]: message }));
  };

  const handleNameChange = (e) => {
    let value = e.target.value;

    if (/^[A-Za-z\s]*$/.test(value)) {
      value = value
        .toLowerCase()
        .split(" ")
        .map((word) =>
          word ? word.charAt(0).toUpperCase() + word.slice(1) : "",
        )
        .join(" ");

      setValue("name", value);
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) setValue("phone", value);
  };

  const validateForm = () => {
    let valid = true;
    Object.keys(form).forEach((field) => {
      validateField(field, form[field]);
      if (!form[field]) valid = false;
    });
    const hasExistingErrors = Object.values(errors).some((e) => e !== "");
    if (hasExistingErrors) valid = false;
    return valid;
  };

  useEffect(() => {
    if (showOtpModal) {
      setTimeout(() => {
        otpRefs.current[0]?.focus();
      }, 100);
    }
  }, [showOtpModal]);


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors before submitting.");
      return;
    }

    setIsSubmitting(true);

    const payload = {
      name: form.name,
      gender: form.gender,
      dob: form.dateofbirth,
      email: form.email,
      mobileNumber: form.phone,
      profileFor: form.profileFor,
    };

    try {
      const resultAction = await dispatch(userRegister(payload));

      if (userRegister.fulfilled.match(resultAction)) {
        const userId =
          resultAction.payload?.userId ||
          resultAction.payload?.id ||
          resultAction.payload?._id;

        if (!userId) {
          toast.error("Registration failed. User ID not received.");
          return;
        }

        setRegisteredUserId(userId);
        setShowOtpModal(true);
        toast.success("OTP sent successfully!");
      } else {
        const errorMsg =
          resultAction.payload?.message ||
          resultAction.error ||
          "Registration failed. Please try again.";
        toast.error(errorMsg);
      }
    } catch (error) {
      toast.error(
        error?.response?.message || error?.message || "Something went wrong."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpChange = (value, index) => {
    // Allow only one digit
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;

    setOtp(newOtp);
    setOtpError("");

    // Move to next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }

    // Move to previous input on delete
    if (!value && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }

    // Auto verify when all 6 digits are entered
    if (newOtp.every((digit) => digit !== "")) {
      setTimeout(() => {
        verifyOtp(newOtp.join(""));
      }, 100);
    }
  };


  const handlePaste = (e) => {
    e.preventDefault();

    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);

    if (!pasted) return;

    const newOtp = pasted.split("");

    while (newOtp.length < 6) {
      newOtp.push("");
    }

    setOtp(newOtp);

    if (pasted.length === 6) {
      setTimeout(() => verifyOtp(), 100);
    } else {
      otpRefs.current[pasted.length]?.focus();
    }
  };
  const verifyOtp = async (otpValue = otp.join("")) => {
    if (isVerifying) return;
    const enteredOtp = otpValue;
    if (enteredOtp.length !== 6) {
      setOtpError("Please enter complete 6-digit OTP");
      return;
    }

    setIsVerifying(true);

    try {
      const resultAction = await dispatch(
        userVerifyOtp({ userId: registeredUserId, otp: enteredOtp }),
      );

      if (userVerifyOtp.fulfilled.match(resultAction)) {
        toast.success("OTP verified! Welcome aboard ");
        setShowOtpModal(false);
        navigate("/user/home");
      } else {
        toast.error(
          resultAction.payload?.message || "Invalid OTP. Please try again.",
        );
      }
    } catch (error) {
      toast.error(error || error?.message || "Something went wrong. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    if (!registeredUserId) return;
    try {
      const payload = {
        name: form.name,
        gender: form.gender,
        dob: form.dateofbirth,
        email: form.email,
        mobileNumber: form.phone,
        profileFor: form.profileFor,
      };
      const resultAction = await dispatch(userRegister(payload));
      if (userRegister.fulfilled.match(resultAction)) {
        setOtp(["", "", "", "", "", ""]);
        setOtpError("");
        toast.success("OTP resent successfully!");
      }
    } catch (error) {
      toast.error(error || "Failed to resend OTP.");
    }
  };

  return (
    <>
      <HeaderNavbar />

      <Container fluid className="register-wrapper">
        <Row className="register-row">
          {/* ── LEFT PANEL ── */}
          <Col lg={6} md={6} className="register-left">
            <div className="register-left-inner">
              <h1 className="register-left-title">
                Where Hearts <br />
                Find Their <span>Forever</span>
              </h1>
              <p className="register-left-desc">
                Trusted matchmaking with verified profiles.
              </p>

            </div>
          </Col>

          {/* ── RIGHT PANEL ── */}
          <Col lg={6} md={6} className="register-right">
            <div className="register-form-box">
              <div className="register-form-header">
                <p className="register-welcome-tag">WELCOME</p>
                <h2 className="register-form-title">Create Profile</h2>
                <p className="register-form-subtitle">
                  Continue your journey to finding the perfect match
                </p>
              </div>

              <Form onSubmit={handleSubmit}>
                {/* NAME + PHONE */}
                <Row>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="register-label">
                        Profile For
                      </Form.Label>
                      <Form.Select
                        className="register-input"
                        value={form.profileFor}
                        onChange={(e) => {
                          const value = e.target.value;

                          setValue("profileFor", value);

                          // Automatically set gender based on profileFor
                          if (value === "Son" || value === "Brother") {
                            setValue("gender", "Male");
                          } else if (value === "Daughter" || value === "Sister") {
                            setValue("gender", "Female");
                          }
                        }}
                      >
                        <option value="" disabled hidden>
                          Select Profile For
                        </option>

                        {getProfileForOptions(form.gender).map((item) => (
                          <option key={item} value={item}>
                            {item}
                          </option>
                        ))}
                      </Form.Select>
                      {errors.profileFor && (
                        <small className="register-error">
                          {errors.profileFor}
                        </small>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="register-label">
                        {getNameLabel(form.profileFor)}
                      </Form.Label>
                      <Form.Control
                        className="register-input"
                        value={form.name}
                        onChange={handleNameChange}
                        placeholder={`Enter ${getNameLabel(form.profileFor)}`}
                        maxLength={50}
                      />
                      {errors.name && (
                        <small className="register-error">{errors.name}</small>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="register-label">Phone</Form.Label>
                      <Form.Control
                        className="register-input"
                        value={form.phone}
                        onChange={handlePhoneChange}
                        placeholder="Enter phone number"
                        maxLength={10}
                      />
                      {errors.phone && (
                        <small className="register-error">{errors.phone}</small>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="register-label">Email</Form.Label>
                      <Form.Control
                        className="register-input"
                        value={form.email}
                        onChange={(e) =>
                          setValue("email", e.target.value.toLowerCase())
                        }
                        placeholder="Enter email"
                        maxLength={40}
                      />
                      {errors.email && (
                        <small className="register-error">{errors.email}</small>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="register-label">Gender</Form.Label>
                      <Form.Select
                        className="register-input"
                        value={form.gender}
                        onChange={(e) => {
                          setValue("gender", e.target.value);
                          setValue("dateofbirth", ""); // Reset DOB when gender changes
                        }}
                      >
                        <option value="" disabled hidden>
                          Select Gender
                        </option>

                        {getGenderOptions(form.profileFor).map((gender) => (
                          <option key={gender} value={gender}>
                            {gender}
                          </option>
                        ))}
                      </Form.Select>
                      {errors.gender && (
                        <small className="register-error">
                          {errors.gender}
                        </small>
                      )}
                    </Form.Group>
                  </Col>


                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="register-label">
                        Date of Birth
                      </Form.Label>
                      <Form.Control
                        type="date"
                        className="register-input"
                        value={form.dateofbirth}
                        min={
                          new Date(
                            new Date().setFullYear(
                              new Date().getFullYear() - 55,
                            ),
                          )
                            .toISOString()
                            .split("T")[0]
                        }
                        max={
                          new Date(
                            new Date().setFullYear(
                              new Date().getFullYear() -
                              (form.gender === "Male" ? 21 : 18)
                            )
                          )
                            .toISOString()
                            .split("T")[0]
                        }
                        onChange={(e) =>
                          setValue("dateofbirth", e.target.value)
                        }
                      />
                      {errors.dateofbirth && (
                        <small className="register-error">
                          {errors.dateofbirth}
                        </small>
                      )}
                    </Form.Group>
                  </Col>


                </Row>

                {/* SUBMIT BUTTON */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="register-submit-btn"
                >
                  {isSubmitting ? "Please wait..." : "Create Profile"}
                </button>

                <p className="register-terms-text">
                  By clicking <strong>Create Profile</strong>, I accept the{" "}
                  <Link to="/terms" className="register-terms-link" target="_blank">
                    Terms &amp; Conditions
                  </Link>
                  .
                </p>

                <p className="register-signin-text">
                  Already registered?{" "}
                  <span
                    className="register-signin-link"
                    onClick={() => setShowLogin(true)}
                  >
                    Sign In
                  </span>
                </p>
              </Form>
            </div>
          </Col>
        </Row>
      </Container>

      {/* ── OTP MODAL ── */}
      <Modal
        show={showOtpModal}
        onHide={() => !isVerifying && setShowOtpModal(false)}
        centered
        size="md"
      >
        <Modal.Body className="otp-modal-body">
          <h3 className="otp-modal-title">Verify OTP</h3>
          <p className="otp-modal-subtitle">
            OTP has been sent to your registered phone/email
          </p>

          <div className="otp-inputs-wrapper">
            {otp.map((digit, index) => (
              <input
                ref={(el) => (otpRefs.current[index] = el)}
                key={index}
                type="text"
                inputMode="numeric"
                autoComplete={index === 0 ? "one-time-code" : "off"}
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(e.target.value, index)}
                onPaste={handlePaste}
                className="otp-input"
              />
            ))}
          </div>

          {otpError && <small className="otp-error">{otpError}</small>}


          {isVerifying && (
            <div className="text-center mt-3">
              <span>Verifying OTP...</span>
            </div>
          )}
          <p className="otp-resend-text">
            Didn't receive OTP?{" "}
            <span className="otp-resend-link" onClick={handleResendOtp}>
              Resend
            </span>
          </p>
        </Modal.Body>
      </Modal>

      <Footer />
      <Login show={showLogin} handleClose={() => setShowLogin(false)} />
    </>
  );
}
